import { ChangeDetectionStrategy, ChangeDetectorRef, Component, DestroyRef, OnDestroy, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule, DatePipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, ParamMap, Router, RouterLink } from '@angular/router';
import { Subject, distinctUntilChanged, finalize, takeUntil } from 'rxjs';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ButtonModule } from 'primeng/button';
import { ProgressBarModule } from 'primeng/progressbar';
import { TagModule } from 'primeng/tag';
import { Loading } from '@components/ui/loading/loading';
import { SecondaryButton } from '@components/button/secondary-button/secondary-button';
import { OrchestrationTableHeadingComponent } from '@app/pages/admin/components/orchestration-table-heading/orchestration-table-heading';
import { CopyableIdentifierComponent } from '@app/pages/admin/components/copyable-identifier/copyable-identifier';
import { CustomPaginatorComponent } from '../../Ui-Kit/custom-paginator/custom-paginator.component';
import {
  MonitoredOperationListItemResponse,
  OperationMonitoringService as OperationMonitoringApiService,
} from '@6grain/masofa-monolith-api-client/common';
import { DateFormatLangPipe } from '@app/utils/pipes/date-format-lang-pipe';
import { OperationKind } from './components/operation-details-dialog/operation-details-dialog';
import { operationDetailsUrl, operationDetailsUrlFromHash } from '@app/pages/admin/operation-details.util';
import { assignmentHostLabel, readAssignmentHost, readAssignmentWorker } from '@app/pages/admin/assignment-host.util';
import { AssignmentHostLabelComponent } from '@app/pages/admin/components/assignment-host-label/assignment-host-label';
type OperationState = NonNullable<MonitoredOperationListItemResponse['state']>;
type AssignmentInitiatorKind = NonNullable<MonitoredOperationListItemResponse['initiatorKind']>;
type AssignmentChannel = NonNullable<MonitoredOperationListItemResponse['channel']>;
type OperationRow = MonitoredOperationListItemResponse & {
  assignmentUrl: string | null;
  hostLabel: string;
  workerId: string | null;
  workerServiceName: string | null;
  workerHostName: string | null;
};

interface MonitoredOperationFilters {
  kind?: OperationKind;
  state?: OperationState;
  initiatorKind?: AssignmentInitiatorKind;
  channel?: AssignmentChannel;
  causationId?: string;
  search?: string;
  createdFrom?: string;
  createdTo?: string;
  page: number;
  pageSize: number;
}

@Component({
  selector: 'app-operation-monitoring-page',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TranslateModule,
    ButtonModule,
    ProgressBarModule,
    TagModule,
    Loading,
    CustomPaginatorComponent,
    DateFormatLangPipe,
    SecondaryButton,
    OrchestrationTableHeadingComponent,
    CopyableIdentifierComponent,
    RouterLink,
    AssignmentHostLabelComponent,
  ],
  templateUrl: './operation-monitoring-page.html',
  styleUrl: './operation-monitoring-page.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [DatePipe],
})
export class OperationMonitoringPageComponent implements OnInit, OnDestroy {
  private readonly monitoringService = inject(OperationMonitoringApiService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);
  private readonly destroy$ = new Subject<void>();
  readonly translate = inject(TranslateService);

  readonly kinds: OperationKind[] = ['Assignment', 'JobExecution'];
  readonly states: OperationState[] = ['Pending', 'Running', 'Completed', 'Failed', 'Cancelled'];
  readonly initiatorKinds: AssignmentInitiatorKind[] = ['User', 'Scheduler', 'Service'];
  readonly channels: AssignmentChannel[] = ['Api', 'Quartz', 'Internal'];

  operations: OperationRow[] = [];
  loading = false;
  errorMessage = '';
  totalRecords = 0;
  first = 0;

  filters: MonitoredOperationFilters = {
    page: 1,
    pageSize: 25,
  };

  ngOnInit(): void {
    const detailsUrl = operationDetailsUrlFromHash(this.route.snapshot.fragment);
    if (detailsUrl) {
      void this.router.navigateByUrl(detailsUrl);
      return;
    }
    this.route.queryParamMap
      .pipe(distinctUntilChanged((a, b) => a.toString() === b.toString()), takeUntilDestroyed(this.destroyRef))
      .subscribe(params => {
        this.applyQueryFilters(params);
        this.refresh();
      });
    this.translate.onLangChange.pipe(takeUntil(this.destroy$)).subscribe(() => this.cdr.markForCheck());
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  loadOperations(): void {
    this.loading = true;
    this.errorMessage = '';
    this.monitoringService
      .monitoringOperationsGet(
        this.filters.kind,
        this.filters.state,
        this.filters.initiatorKind,
        this.filters.channel,
        this.filters.search,
        undefined,
        this.filters.causationId,
        undefined,
        this.filters.createdFrom ? new Date(this.filters.createdFrom).toISOString() : undefined,
        this.filters.createdTo ? new Date(this.filters.createdTo).toISOString() : undefined,
        undefined,
        undefined,
        this.filters.page,
        this.filters.pageSize,
      )
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          this.loading = false;
          this.cdr.markForCheck();
        }),
      )
      .subscribe({
        next: response => {
          this.operations = (response.items ?? []).map(item => ({
            ...item,
            assignmentUrl: item.kind === 'Assignment' && item.id
              ? operationDetailsUrl('Assignment', item.id)
              : null,
            hostLabel: assignmentHostLabel({
              kind: item.kind,
              startedAt: item.startedAt,
              ...readAssignmentHost(item),
            }),
            ...readAssignmentWorker(item),
          }));
          this.totalRecords = response.totalCount ?? 0;
          this.first = ((response.page ?? 1) - 1) * (response.pageSize ?? this.filters.pageSize);
        },
        error: () => {
          this.operations = [];
          this.totalRecords = 0;
          this.errorMessage = 'Failed to load operations';
        },
      });
  }

  refresh(): void {
    this.loadOperations();
  }

  resetFilters(): void {
    this.filters = { page: 1, pageSize: this.filters.pageSize };
    this.first = 0;
    this.loadOperations();
  }

  onHeaderSearch(query: string): void {
    this.filters.search = query.trim() || undefined;
    this.filters.page = 1;
    this.first = 0;
    this.loadOperations();
  }

  onPageChange(event: { first?: number; rows?: number }): void {
    const first = event.first ?? 0;
    const rows = event.rows ?? this.filters.pageSize;
    this.first = first;
    this.filters.pageSize = rows;
    this.filters.page = Math.floor(first / rows) + 1;
    this.loadOperations();
  }

  onPageSizeChange(pageSize: number): void {
    this.filters.pageSize = pageSize;
    this.filters.page = 1;
    this.first = 0;
    this.loadOperations();
  }

  openDetails(operation: MonitoredOperationListItemResponse): void {
    if (!operation.kind || !operation.id) return;
    void this.router.navigateByUrl(operationDetailsUrl(operation.kind, operation.id));
  }

  getStateSeverity(state?: string | null): 'success' | 'info' | 'warn' | 'danger' | 'secondary' {
    switch (state) {
      case 'Completed':
      case 'Succeeded':
        return 'success';
      case 'Running':
        return 'info';
      case 'Pending':
      case 'Warning':
        return 'warn';
      case 'Failed':
      case 'Error':
      case 'Critical':
        return 'danger';
      default:
        return 'secondary';
    }
  }

  formatDuration(seconds?: number | null): string {
    if (seconds === undefined || seconds === null) {
      return '—';
    }
    if (seconds < 60) {
      return `${Math.round(seconds)} s`;
    }
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = Math.round(seconds % 60);
    return `${minutes} min ${remainingSeconds} s`;
  }

  private applyQueryFilters(params: ParamMap): void {
    const kind = params.get('kind');
    const search = params.get('search');
    this.filters.kind = kind === 'Assignment' || kind === 'JobExecution' ? kind : undefined;
    this.filters.search = search?.trim() || undefined;
    this.filters.causationId = params.get('causationId')?.trim() || undefined;
    this.filters.page = 1;
    this.first = 0;
  }
}
